export interface ResolvedDeliveryDate {
  displayed: string | null;
  systemDueDate: string | null;
  userDueDate: string | null;
  isUserDefined: boolean;
}

function presentDate(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Prefer a user-defined delivery date, and keep the system date available beside it. */
export function resolveDisplayedDeliveryDate(
  dueDate: string | null | undefined,
  userDueDate: string | null | undefined,
): ResolvedDeliveryDate {
  const systemDueDate = presentDate(dueDate);
  const customDueDate = presentDate(userDueDate);

  if (customDueDate) {
    return {
      displayed: customDueDate,
      systemDueDate,
      userDueDate: customDueDate,
      isUserDefined: true,
    };
  }

  return {
    displayed: systemDueDate,
    systemDueDate,
    userDueDate: null,
    isUserDefined: false,
  };
}

export type DeliveryDateSource = "system" | "user";

export function formatDeliveryDateForSave(date: string): string {
  return date.trim().replace("T", " ").replace("Z", "").split(".")[0];
}

/** Which stored date is currently shown. The system date is never overwritten by this choice. */
export function displayedDeliveryDate(
  dates: ResolvedDeliveryDate,
  source: DeliveryDateSource,
): string | null {
  if (dates.isUserDefined && source === "system" && dates.systemDueDate) {
    return dates.systemDueDate;
  }
  return dates.userDueDate ?? dates.systemDueDate;
}

/**
 * Keep the original/custom pair when a refresh echoes the saved date into `dueDate`
 * or omits `userDueDate` after the user has already overridden it.
 */
export function retainDeliveryDatePair(
  previous: { systemDueDate: string | null; userDueDate: string | null },
  dueDate: string | null | undefined,
  userDueDate: string | null | undefined,
  userUpdated: boolean,
): ResolvedDeliveryDate {
  const incoming = resolveDisplayedDeliveryDate(dueDate, userDueDate);
  if (incoming.isUserDefined) return incoming;

  if (userDueDate === undefined && previous.userDueDate) {
    return {
      displayed: previous.userDueDate,
      systemDueDate: previous.systemDueDate ?? incoming.systemDueDate,
      userDueDate: previous.userDueDate,
      isUserDefined: true,
    };
  }

  if (!userUpdated || !previous.userDueDate) return incoming;

  const incomingSystem = incoming.systemDueDate;
  if (
    incomingSystem &&
    incomingSystem === previous.userDueDate &&
    previous.systemDueDate &&
    previous.systemDueDate !== previous.userDueDate
  ) {
    return {
      displayed: previous.userDueDate,
      systemDueDate: previous.systemDueDate,
      userDueDate: previous.userDueDate,
      isUserDefined: true,
    };
  }

  return {
    displayed: previous.userDueDate,
    systemDueDate: previous.systemDueDate ?? incomingSystem,
    userDueDate: previous.userDueDate,
    isUserDefined: true,
  };
}
